import React, { useState, useCallback, useEffect, memo } from 'react';
import UnderlineTabMenu from '../common/menu/UnderlineTabMenu';
import Button from '../common/button/Button';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { DEFAULT_MODEL } from '../../constants/models';
import { updateAgent, getAgentDetailsForOwner } from '../../api/agents/agents';
import UnsavedChangesBar from './settings/UnsavedChangesBar';

// Tabs
import AgentGeneralTab from './settings/AgentGeneralTab';
import AgentTriggersTab from './settings/AgentTriggersTab';
import AgentToolsTab from './settings/AgentToolsTab';
import AgentSafetyTab from './settings/AgentSafetyTab';
import AgentMemoryTab from './settings/AgentMemoryTab';
import AgentFilesTab from './settings/AgentFilesTab';

import styles from './styles/AgentSettings.module.css';

// Max tool steps settings
const MIN_MAX_TOOL_STEPS = 5;
const MAX_MAX_TOOL_STEPS = 30;
const DEFAULT_MAX_TOOL_STEPS = 10;

// Default system prompt
const DEFAULT_SYSTEM_PROMPT = `You are a helpful assistant. You are able to use tools to help the user.`;

const TABS = [
    { id: 'general', label: 'Agent' },
    { id: 'triggers', label: 'Triggers' },
    { id: 'tools', label: 'Tools' },
    { id: 'safety', label: 'Safety' },
    { id: 'memory', label: 'Memory' },
    { id: 'files', label: 'Files' },
];

const AgentSettings = ({ agent, onBack }) => {
    const [activeTab, setActiveTab] = useState('general');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showExitConfirmation, setShowExitConfirmation] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Initial form data - this serves as our baseline for comparison
    const getInitialFormData = useCallback((sourceAgent = agent) => ({
        general: {
            name: sourceAgent?.name || '',
            slug: sourceAgent?.slug || '',
            description: sourceAgent?.description || '',
            avatar: sourceAgent?.logo || null,
            systemPrompt: sourceAgent?.systemPrompt || DEFAULT_SYSTEM_PROMPT,
            welcomeMessage: (sourceAgent?.welcomeMessage ?? ''),
            model: sourceAgent?.model || DEFAULT_MODEL,
            accessType: sourceAgent?.accessType || 'PRIVATE',
            creditsPayer: sourceAgent?.creditsPayer || 'OWNER',
        },
        triggers: {
            webhooks: sourceAgent?.webhooks || [],
            tasks: sourceAgent?.tasks || [],
        },
        tools: {
            maxToolSteps: sourceAgent?.maxToolSteps || DEFAULT_MAX_TOOL_STEPS,
            toolsVisibility: sourceAgent?.toolsVisibility || 'ALL',
            selectedTools: sourceAgent?.tools || [],
        },
        safety: {
            enableSafetyChecks: sourceAgent?.enableSafetyChecks || false,
            customSafetyPrompt: sourceAgent?.customSafetyPrompt || '',
        },
        memory: {
            memoryEnabled: sourceAgent?.memoryEnabled ?? false,
        },
        files: {},
    }), [agent]);

    const [formData, setFormData] = useState(getInitialFormData());
    // Persisted baseline snapshot to compare against (not recomputed from props)
    const [initialFormDataSnapshot, setInitialFormDataSnapshot] = useState(getInitialFormData());

    // Update form data when agent changes
    useEffect(() => {
        const initialData = getInitialFormData();
        setFormData(initialData);
        setInitialFormDataSnapshot(initialData);
        setHasUnsavedChanges(false);
    }, [agent, getInitialFormData]);

    // Fetch fresh agent details (owner-only) to populate all fields
    useEffect(() => {
        const fetchDetails = async () => {
            if (!agent?.slug) return;
            try {
                const fresh = await getAgentDetailsForOwner(agent.slug);
                // Backward compatible mapping: if grouped, map accordingly
                const mapped = fresh?.general ? {
                    ...fresh.general,
                    maxToolSteps: fresh?.tools?.maxToolSteps,
                    toolsVisibility: fresh?.tools?.toolsVisibility || 'ALL',
                    accessType: fresh?.meta?.accessType ?? fresh?.accessType,
                    creditsPayer: fresh?.meta?.creditsPayer ?? fresh?.creditsPayer,
                    memoryEnabled: (fresh?.memory?.memoryEnabled ?? fresh?.memoryEnabled ?? false),
                } : fresh;
                const initialFromFresh = getInitialFormData(mapped);
                setFormData(initialFromFresh);
                setInitialFormDataSnapshot(initialFromFresh);
                setHasUnsavedChanges(false);
            } catch (err) {
                console.error('Failed to fetch agent details:', err);
            }
        };
        fetchDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agent?.slug]);

    // Handle beforeunload event to warn about unsaved changes
    useEffect(() => {
        const handleBeforeUnload = e => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleTabChange = useCallback(tabId => {
        setActiveTab(tabId);
    }, []);

    // Deep comparison function to check if objects are equal
    const deepEqual = useCallback((obj1, obj2) => {
        if (obj1 === obj2) return true;

        if (obj1 == null || obj2 == null) return false;

        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) return false;

        for (let key of keys1) {
            if (!keys2.includes(key)) return false;

            if (!deepEqual(obj1[key], obj2[key])) return false;
        }

        return true;
    }, []);

    const handleFormChange = useCallback((section, data) => {
        const newFormData = {
            ...formData,
            [section]: {
                ...formData[section],
                ...data,
            },
        };

        setFormData(newFormData);

        // Check if there are actual changes compared to stored baseline snapshot
        const hasChanges = !deepEqual(newFormData, initialFormDataSnapshot);
        setHasUnsavedChanges(hasChanges);
    }, [formData, initialFormDataSnapshot, deepEqual]);

    const handleSave = useCallback(async () => {
        if (!agent?.slug) {
            console.error('Agent slug is required for saving');
            return;
        }

        setIsSaving(true);
        setSaveError(null);

        // Clamp maxToolSteps to sensible bounds
        const clampedMaxToolSteps =
            Math.max(MIN_MAX_TOOL_STEPS, Math.min(MAX_MAX_TOOL_STEPS, formData.tools.maxToolSteps));

        try {
            // Save Agent (general) and Tools: maxToolSteps
            const trimmedWelcome = (formData.general.welcomeMessage || '').trim();
            const updateData = {
                name: formData.general.name,
                description: formData.general.description,
                systemPrompt: formData.general.systemPrompt,
                welcomeMessage: trimmedWelcome.length > 0 ? trimmedWelcome : null,
                model: formData.general.model,
                maxToolSteps: clampedMaxToolSteps,
                toolsVisibility: formData.tools.toolsVisibility,
                accessType: formData.general.accessType,
                creditsPayer: formData.general.creditsPayer,
                memoryEnabled: !!formData.memory.memoryEnabled,
            };

            const saved = await updateAgent(agent.slug, updateData);
            // Refresh baseline snapshot with what we just saved (merge back to current form)
            const newBaseline = {
                ...formData,
                general: {
                    ...formData.general,
                    name: saved?.name ?? updateData.name,
                    description: saved?.description ?? updateData.description,
                    systemPrompt: saved?.systemPrompt ?? updateData.systemPrompt,
                    welcomeMessage: (saved?.welcomeMessage ?? updateData.welcomeMessage ?? ''),
                    model: saved?.model ?? updateData.model,
                    accessType: saved?.accessType ?? updateData.accessType,
                    creditsPayer: saved?.creditsPayer ?? updateData.creditsPayer,
                },
                tools: {
                    ...formData.tools,
                    maxToolSteps: saved?.maxToolSteps ?? updateData.maxToolSteps,
                    toolsVisibility: saved?.toolsVisibility ?? updateData.toolsVisibility,
                },
                memory: {
                    ...formData.memory,
                    memoryEnabled: saved?.memoryEnabled ?? updateData.memoryEnabled,
                },
            };
            setInitialFormDataSnapshot(newBaseline);
            setHasUnsavedChanges(false);
            setSaveError(null);
            console.log('Agent settings saved successfully');
        } catch (error) {
            console.error('Error saving agent settings:', error);
            setSaveError(error.message || 'Failed to save agent settings');
        } finally {
            setIsSaving(false);
        }
    }, [agent, formData]);

    const handleClear = useCallback(() => {
        setFormData(initialFormDataSnapshot);
        setHasUnsavedChanges(false);
        setSaveError(null);
    }, [initialFormDataSnapshot]);

    const handleBackClick = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowExitConfirmation(true);
        } else {
            onBack();
        }
    }, [hasUnsavedChanges, onBack]);

    const handleConfirmExit = useCallback(() => {
        setShowExitConfirmation(false);
        onBack();
    }, [onBack]);

    const handleCancelExit = useCallback(() => {
        setShowExitConfirmation(false);
    }, []);

    const renderTabContent = () => {
        switch (activeTab) {
            case 'general':
                return (
                    <AgentGeneralTab
                        data={formData.general}
                        onChange={data => handleFormChange('general', data)}
                    />
                );
            case 'triggers':
                return (
                    <AgentTriggersTab
                        data={formData.triggers}
                        onChange={data => handleFormChange('triggers', data)}
                    />
                );
            case 'tools':
                return (
                    <AgentToolsTab
                        data={formData.tools}
                        onChange={data => handleFormChange('tools', data)}
                        agentId={agent?.id}
                    />
                );
            case 'safety':
                return (
                    <AgentSafetyTab
                        data={formData.safety}
                        onChange={data => handleFormChange('safety', data)}
                    />
                );
            case 'memory':
                return (
                    <AgentMemoryTab
                        data={formData.memory}
                        onChange={data => handleFormChange('memory', data)}
                    />
                );
            case 'files':
                return (
                    <AgentFilesTab
                        data={formData.files}
                        onChange={data => handleFormChange('files', data)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>Agent Configuration</h2>
                <Button onClick={handleBackClick} variant="secondary" size="small">
                    ← Back to Agent
                </Button>
            </div>

            <div className={styles.tabsContainer}>
                <UnderlineTabMenu tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />
            </div>

            <div className={styles.content}>
                {renderTabContent()}
                <UnsavedChangesBar 
                    onSave={handleSave} 
                    onClear={handleClear} 
                    isVisible={hasUnsavedChanges}
                    isSaving={isSaving}
                    saveError={saveError}
                />
            </div>

            <ConfirmationDialog
                isOpen={showExitConfirmation}
                onClose={handleCancelExit}
                onConfirm={handleConfirmExit}
                title="Unsaved Changes"
                message="You have unsaved changes. Are you sure you want to leave without saving?"
                confirmText="Leave Without Saving"
                cancelText="Stay"
                dangerous={true}
            />
        </div>
    );
};

export default memo(AgentSettings);
